import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import {
  fmtMoney,
  type GraphDataRow,
  type TableDataRow,
} from "app/pages/InteractiveReviewCenter/pages/performanceChartModel";

export interface InterestCreditsPanelProps {
  isDark: boolean;
  cardBg: string;
  divider: string;
  graphData: GraphDataRow[];
  tableData: TableDataRow[];
  height?: number;
  enableAgeFilter?: boolean;
}

const ACTUAL_COLOR = "#004781";
const PROJ_COLOR = "#9E9E9E";

const InterestCreditsPanel = React.memo((props: InterestCreditsPanelProps) => {
  const {
    isDark,
    cardBg,
    divider,
    graphData,
    tableData,
    height = 320,
    enableAgeFilter = true,
  } = props;

  const tableByPlanYear = useMemo(() => {
    const m = new Map<number, TableDataRow>();
    tableData.forEach((t) => m.set(t.planYear, t));
    return m;
  }, [tableData]);

  const option = useMemo(() => {
    const textColor = isDark ? "#e2e8f0" : "#334155";
    const gridColor = isDark ? "#3A3A50" : "#e2e8f0";
    const rows = graphData;
    const xLabels = enableAgeFilter
      ? rows.map((r) => `Age ${r.insuredAge}`)
      : rows.map((r) => `Year ${r.planYear}`);

    if (!rows.length) {
      return {
        title: {
          text: "No interest data",
          left: "center",
          top: "middle",
          textStyle: { color: textColor, fontSize: 14 },
        },
      };
    }

    const seriesData = rows.map((r) => {
      const tRow = tableByPlanYear.get(r.planYear);
      const hasActual = tRow?.actualInterestCredits != null;
      const val = hasActual
        ? Number(tRow!.actualInterestCredits)
        : Number(r.interestCredits ?? tRow?.interestCredits ?? 0);

      if (!hasActual) {
        return {
          value: val,
          itemStyle: {
            color: PROJ_COLOR,
            borderRadius: [6, 6, 0, 0],
          },
          label: { show: false },
        };
      }

      return {
        value: val,
        itemStyle: {
          color: ACTUAL_COLOR,
          borderRadius: [6, 6, 0, 0],
        },
        label: {
          show: val > 0,
          position: "top" as const,
          color: "#004781",
          fontWeight: "bold" as const,
          fontSize: 16,
          fontFamily: "Conthrax Semi-Bold",
          formatter: () => fmtMoney(val),
        },
      };
    });

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        backgroundColor: isDark ? "#2A2A3D" : "#ffffff",
        borderColor: isDark ? "#3A3A50" : "#E5E7EB",
        textStyle: { color: textColor },
        formatter: (params: { name: string; dataIndex: number }[]) => {
          const p = params[0];
          if (!p) return "";
          const row = rows[p.dataIndex];
          const tRow = tableByPlanYear.get(row.planYear);
          const hasActual = tRow?.actualInterestCredits != null;
          const val = hasActual
            ? Number(tRow!.actualInterestCredits)
            : Number(row.interestCredits ?? 0);
          return `<div style="font-weight:600">${p.name}</div><div>${
            hasActual ? fmtMoney(val) : "<em style='color:#9CA3AF'>Projected</em>"
          }</div>`;
        },
      },
      grid: { left: 48, right: 4, top: 20, bottom: 52, containLabel: true },
      xAxis: {
        type: "category",
        data: xLabels,
        axisLine: { lineStyle: { color: gridColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, fontSize: 10 },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: textColor,
          fontSize: 10,
          formatter: (v: number) => (v >= 1000 ? `$${v / 1000}K` : `$${v}`),
        },
        splitLine: { lineStyle: { color: gridColor, type: "dashed" as const } },
      },
      dataZoom: [
        {
          type: "slider",
          bottom: 6,
          height: 18,
          borderColor: isDark ? "#3A3A50" : "#E5E7EB",
          fillerColor: isDark ? "rgba(96,165,250,0.18)" : "rgba(30,58,138,0.10)",
          handleStyle: { color: "#1E3A8A", borderColor: "#1E3A8A" },
          moveHandleStyle: { color: "#1E3A8A" },
          textStyle: { color: textColor, fontSize: 9 },
          dataBackground: {
            lineStyle: { color: isDark ? "#4B5563" : "#CBD5E1" },
            areaStyle: {
              color: isDark ? "rgba(75,85,99,0.3)" : "rgba(30,58,138,0.06)",
            },
          },
          start: 0,
          end: rows.length > 10 ? Math.round((10 / rows.length) * 100) : 100,
        },
      ],
      series: [
        {
          type: "bar",
          barMaxWidth: 56,
          barCategoryGap: "25%",
          data: seriesData,
        },
      ],
    };
  }, [graphData, isDark, tableByPlanYear, enableAgeFilter]);

  return (
    <div
      className={`rounded-2xl overflow-hidden border shadow-sm ${
        isDark ? "border-[#3A3A50] shadow-none" : "border-slate-200/90 shadow-md"
      } ${cardBg}`}
    >
      <div className={`flex items-center px-4 py-3 border-b ${divider}`} />
      <div className="px-2 sm:px-3 pt-2 pb-3 w-full min-w-0">
        <ReactECharts
          option={option}
          style={{ width: "100%", height, minHeight: height }}
          className="w-full min-w-0"
          notMerge
          lazyUpdate
        />
      </div>
    </div>
  );
});

InterestCreditsPanel.displayName = "InterestCreditsPanel";

export default InterestCreditsPanel;
